import { useId, useMemo, useState } from 'react';
import {
  allAlternativeShapes,
  enumerateAlternativeShapes,
  validateAlternativeShape,
  type AlternativeShape,
  type AlternativeStringLayout,
} from '@/music/alternativeShapes';
import { chordSymbol, type ChordTypeId } from '@/music/chords';
import { fretsOnLayout, layoutOfFingering, windowStartFor } from '@/music/placedVoicing';
import { gripFromFrets, type Fingering } from '@/music/voicing';
import { useStore } from '@/state/store';
import { FretboardEditor } from './VoicingEditors';
import { PlayHoldButton } from './PlayHoldButton';
import { PlayableDiagram } from './PlayableDiagram';
import { Modal } from './Modal';
import './alternative-shapes.css';

interface AlternativeShapeEditorProps {
  rootDisplay: string;
  chordType: ChordTypeId;
  initialFingering: Fingering;
  /** Only user shapes are edited in place; built-ins can be saved as a new copy. */
  shape?: AlternativeShape;
  onClose: () => void;
  onSaved?: (shape: AlternativeShape) => void;
}

/** Shared by library rows and the inspector, including chords with no tune pin. */
export function AlternativeShapeEditor({
  rootDisplay,
  chordType,
  initialFingering,
  shape,
  onClose,
  onSaved,
}: AlternativeShapeEditorProps) {
  const saveAlternativeShape = useStore((s) => s.saveAlternativeShape);
  const setAlternativePreferred = useStore((s) => s.setAlternativePreferred);
  const preferredIds = useStore((s) => s.preferredAlternativeIds);
  const helpId = useId();
  const sourceLayout = shape?.stringLayout ?? layoutOfFingering(initialFingering);
  const seedLayout: AlternativeStringLayout = sourceLayout === 'joe-pass' ? 'adgb' : sourceLayout;
  const seedFrets = shape?.frets ?? fretsOnLayout(initialFingering.frets, seedLayout);
  const [id] = useState(() => shape?.id ?? `alternative-${crypto.randomUUID()}`);
  const [name, setName] = useState(
    shape?.name ?? `${chordSymbol(rootDisplay, chordType)} alternative`,
  );
  const [layout, setLayout] = useState(seedLayout);
  const [frets, setFrets] = useState<(number | null)[]>(() => [...seedFrets]);
  const [windowStart, setWindowStart] = useState(() => windowStartFor(seedFrets));
  const [movable, setMovable] = useState(
    shape ? shape.transposition === 'movable' : !seedFrets.includes(0),
  );
  const [preferred, setPreferred] = useState(shape ? preferredIds.includes(shape.id) : true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const unchangedGrip =
    layout === seedLayout && frets.every((fret, index) => fret === seedFrets[index]);
  const construction = unchangedGrip
    ? (shape?.construction ?? initialFingering.alternativeShape?.construction)
    : undefined;
  const candidate: AlternativeShape = {
    id,
    name: name.trim(),
    root: rootDisplay,
    chordType,
    frets,
    stringLayout: layout,
    transposition: movable ? 'movable' : 'fixed',
    ...(construction ? { construction } : {}),
  };
  const error = validateAlternativeShape(candidate);
  const preview = useMemo(
    () => gripFromFrets(rootDisplay, chordType, frets, layout),
    [rootDisplay, chordType, frets, layout],
  );

  const save = () => {
    if (error) return;
    if (!saveAlternativeShape(candidate)) {
      setSaveError('This shape could not be saved. Check its name and notes.');
      return;
    }
    setAlternativePreferred(id, preferred);
    onSaved?.(candidate);
    onClose();
  };

  return (
    <div className="alternative-shape-editor" data-testid="alternative-shape-editor">
      <label className="alternative-shape-field">
        <span>Shape name</span>
        <input
          className="text-input"
          aria-label="Shape name"
          value={name}
          maxLength={80}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <p className="muted">
        For {chordSymbol(rootDisplay, chordType)}. Place the four notes of your shape.
      </p>
      <FretboardEditor
        rootDisplay={rootDisplay}
        chordType={chordType}
        layout={layout}
        onLayoutChange={(next) => {
          if (next === 'joe-pass') return;
          setLayout(next);
          setFrets(fretsOnLayout(frets, next));
        }}
        frets={frets}
        onFretsChange={(next) => {
          if (!frets.includes(0) && next.includes(0)) setMovable(false);
          setFrets(next);
        }}
        windowStart={windowStart}
        onWindowStartChange={setWindowStart}
        allowAllStrings={false}
      />
      <div className="alternative-shape-settings">
        <label className="alternative-shape-check">
          <input
            type="checkbox"
            checked={movable}
            onChange={(event) => setMovable(event.target.checked)}
            aria-describedby={helpId}
          />
          Move this shape to other keys
        </label>
        <p className="muted" id={helpId}>
          {movable
            ? 'The whole shape moves together. Open strings become fretted notes when moved.'
            : `Keep these exact frets for ${chordSymbol(rootDisplay, chordType)} only.`}
        </p>
        <label className="alternative-shape-check">
          <input
            type="checkbox"
            checked={preferred}
            onChange={(event) => setPreferred(event.target.checked)}
          />
          Prefer in arrangements
        </label>
        <p className="muted">
          Prefer when the harmony and melody fit. Standard voicings stay available.
        </p>
      </div>
      {(error || saveError) && (
        <p className="alternative-shape-error" role="status">
          {error || saveError}
        </p>
      )}
      <div className="row alternative-shape-actions">
        <PlayHoldButton
          fingering={preview ?? initialFingering}
          stringSet={preview?.stringSet ?? initialFingering.stringSet}
          className="btn btn-sm"
          disabled={!preview}
          label="Play"
        />
        <span className="row alternative-shape-buttons">
          <button type="button" className="btn btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!!error}
            onClick={save}
          >
            Save shape
          </button>
        </span>
      </div>
    </div>
  );
}

interface LibraryAlternativeShapesProps {
  rootDisplay: string;
  chordType: ChordTypeId;
  initialFingering: Fingering;
  highlightAvoid: boolean;
}

/** Alternatives live alongside the four strict inversions, with the same play gesture. */
export function LibraryAlternativeShapes({
  rootDisplay,
  chordType,
  initialFingering,
  highlightAvoid,
}: LibraryAlternativeShapesProps) {
  const custom = useStore((s) => s.alternativeShapes);
  const preferredIds = useStore((s) => s.preferredAlternativeIds);
  const setPreferred = useStore((s) => s.setAlternativePreferred);
  const remove = useStore((s) => s.removeAlternativeShape);
  const [editing, setEditing] = useState<AlternativeShape | 'new' | null>(null);
  const shapes = useMemo(() => allAlternativeShapes(custom), [custom]);
  const entries = useMemo(
    () =>
      shapes
        .filter((shape) => shape.chordType === chordType)
        .flatMap((shape) => {
          const candidates = enumerateAlternativeShapes(rootDisplay, chordType, [shape]);
          // One diagram per shape. Show the closest available copy to its saved position.
          const fretDistance = (fingering: Fingering) =>
            fingering.frets.reduce<number>(
              (sum, fret, index) => sum + Math.abs((fret ?? 0) - (shape.frets[index] ?? 0)),
              0,
            );
          const fingering = candidates.sort((a, b) => fretDistance(a) - fretDistance(b))[0];
          return fingering ? [{ shape, fingering }] : [];
        }),
    [shapes, rootDisplay, chordType],
  );

  return (
    <div className="library-alternatives">
      <div className="row library-alternatives-head">
        {entries.length > 0 && <h4>Alternative shapes</h4>}
        <button
          type="button"
          className="btn btn-sm"
          aria-label={`Add alternative shape for ${chordSymbol(rootDisplay, chordType)}`}
          onClick={() => setEditing('new')}
        >
          Add alternative shape
        </button>
      </div>
      {entries.length > 0 && (
        <div className="library-alternatives-grid">
          {entries.map(({ shape, fingering }) => {
            const editable = custom.some((entry) => entry.id === shape.id);
            return (
              <div
                key={shape.id}
                className="library-alternative"
                data-testid={`alternative-shape-${shape.id}`}
              >
                <div className="library-inversion-grid">
                  <PlayableDiagram
                    chord={{
                      fingering,
                      rootDisplay,
                      chordType,
                      symbol: fingering.symbol,
                      stringSet: fingering.stringSet,
                    }}
                    caption={shape.name}
                    showTitle={false}
                    highlightAvoid={highlightAvoid}
                  />
                </div>
                <label className="alternative-shape-check">
                  <input
                    type="checkbox"
                    checked={preferredIds.includes(shape.id)}
                    onChange={(event) => setPreferred(shape.id, event.target.checked)}
                  />
                  Prefer in arrangements
                </label>
                {shape.transposition === 'fixed' && <span className="muted">Fixed position</span>}
                {editable && (
                  <div className="row alternative-shape-buttons">
                    <button type="button" className="btn btn-sm" onClick={() => setEditing(shape)}>
                      Edit
                    </button>
                    <button type="button" className="btn btn-sm" onClick={() => remove(shape.id)}>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {editing && (
        <Modal
          title={`${editing === 'new' ? 'Add' : 'Edit'} alternative shape · ${chordSymbol(editing === 'new' ? rootDisplay : editing.root, chordType)}`}
          onClose={() => setEditing(null)}
        >
          <AlternativeShapeEditor
            key={editing === 'new' ? 'new' : editing.id}
            rootDisplay={editing === 'new' ? rootDisplay : editing.root}
            chordType={chordType}
            initialFingering={initialFingering}
            shape={editing === 'new' ? undefined : editing}
            onClose={() => setEditing(null)}
          />
        </Modal>
      )}
    </div>
  );
}
